import React, { useEffect, useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, Store, Wifi, WifiOff, Bell, RefreshCw } from 'lucide-react';
import { posApi } from '../api/pos.api';
import toast from 'react-hot-toast';
import { useSocket } from '../context/SocketContext';
import { getPendingCount } from '../api/offlineQueue';

export const POSTerminalLayout = () => {
  const { user } = useAuth();
  const [isLocked, setIsLocked] = useState(true);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const { socket } = useSocket();
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlinePending, setOfflinePending] = useState(0);

  // Track online/offline status + pending queue count
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    const updatePending = () => {
      getPendingCount().then(setOfflinePending);
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    window.addEventListener('offline-queue-changed', updatePending);

    updatePending();
    const interval = setInterval(updatePending, 5000);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('offline-queue-changed', updatePending);
      clearInterval(interval);
    };
  }, []);

  // Notifications initial fetch
  useEffect(() => {
    if (!user) return;
    import('../api/axios').then((mod) => {
      mod.api
        .get('/notifications?limit=50')
        .then((res) => {
          const raw = res.data;
          const data = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
          setUnreadNotifs(data.filter((n: any) => !n.is_read).length);
        })
        .catch(() => {});
    });
  }, [user]);

  // Socket notifications listener
  useEffect(() => {
    if (!socket) return;
    const handler = () => setUnreadNotifs((p) => p + 1);
    socket.on('notification', handler);
    return () => {
      socket.off('notification', handler);
    };
  }, [socket]);

  // Live clock
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (!user) {
    return <Navigate to="/login" />;
  }

  const handleNumpad = (num: string) => {
    if (pinInput.length < 4) {
      const newPin = pinInput + num;
      setPinInput(newPin);
      setPinError(false);

      // Auto-submit when 4 digits entered
      if (newPin.length === 4) {
        handleUnlock(newPin);
      }
    }
  };

  async function handleUnlock(pin: string) {
    setVerifying(true);
    try {
      await posApi.verifyPin(pin);
      setIsLocked(false);
      setPinInput('');
      setPinError(false);
    } catch {
      setPinError(true);
      setTimeout(() => {
        setPinInput('');
        setPinError(false);
      }, 600);
    } finally {
      setVerifying(false);
    }
  }

  const timeStr = currentTime.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const dateStr = currentTime.toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' });

  const handleForceUpdate = () => {
    toast('Обновление приложения...', { icon: '🔄' });
    if ('caches' in window) {
      caches.keys().then((names) => {
        Promise.all(names.map((name) => caches.delete(name))).then(() => {
          window.location.reload();
        });
      });
    } else {
      window.location.reload();
    }
  };

  if (isLocked) {
    return (
      <div className="fixed inset-0 bg-linear-to-br from-slate-200 via-slate-100 to-slate-200 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 z-50 flex items-center justify-center flex-col text-slate-900 dark:text-white select-none">
        {/* Background decorations */}
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[40%] bg-blue-600/5 rounded-full blur-[150px] pointer-events-none" />
        <div className="absolute bottom-[10%] right-[5%] w-[40%] h-[40%] bg-emerald-600/5 rounded-full blur-[150px] pointer-events-none" />

        {/* Clock */}
        <div className="text-center mb-8">
          <p className="text-6xl font-extralight text-slate-800/90 dark:text-white/90 tracking-wide">{timeStr}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 uppercase tracking-widest mt-2">{dateStr}</p>
        </div>

        {/* Avatar + Name */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-20 h-20 rounded-full bg-linear-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-3xl font-bold shadow-xl shadow-blue-500/20 mb-3">
            {(user.name || 'К')[0]}
          </div>
          <p className="text-lg font-medium text-slate-800/90 dark:text-white/90">{user.name || 'Кассир'}</p>
          <p className="text-xs text-slate-500 dark:text-slate-500 uppercase tracking-wider">Введите ПИН-код</p>
        </div>

        {/* PIN Dots */}
        <div className="flex gap-4 mb-8">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full transition-all duration-200 ${
                pinError
                  ? 'bg-red-500 animate-pulse'
                  : i < pinInput.length
                    ? 'bg-slate-800 dark:bg-white scale-110'
                    : 'bg-slate-300 dark:bg-slate-700'
              }`}
            />
          ))}
        </div>

        {/* Numpad */}
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              onClick={() => handleNumpad(num.toString())}
              disabled={verifying}
              className="w-20 h-20 rounded-sm bg-slate-200/60 dark:bg-white/5 backdrop-blur-xs text-2xl font-medium hover:bg-slate-300/80 dark:hover:bg-white/10 active:bg-slate-400/60 dark:active:bg-white/15 transition-all duration-150 border border-slate-300 dark:border-white/5 disabled:opacity-50"
            >
              {num}
            </button>
          ))}
          <div />
          <button
            onClick={() => handleNumpad('0')}
            disabled={verifying}
            className="w-20 h-20 rounded-sm bg-slate-200/60 dark:bg-white/5 backdrop-blur-xs text-2xl font-medium hover:bg-slate-300/80 dark:hover:bg-white/10 active:bg-slate-400/60 dark:active:bg-white/15 transition-all duration-150 border border-slate-300 dark:border-white/5 disabled:opacity-50"
          >
            0
          </button>
          <button
            onClick={() => setPinInput((p) => p.slice(0, -1))}
            disabled={verifying}
            className="w-20 h-20 rounded-sm text-xl hover:bg-slate-200/60 dark:hover:bg-white/5 transition-all text-slate-500 dark:text-slate-400"
          >
            ⌫
          </button>
        </div>

        {verifying && <p className="mt-6 text-sm text-slate-500 dark:text-slate-400 animate-pulse">Проверяем...</p>}
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-50 dark:bg-[#0f172a] flex flex-col overflow-hidden">
      {/* POS Header */}
      <header className="bg-white dark:bg-[#1e293b] border-b border-slate-200 dark:border-slate-700/50 px-4 py-2 flex justify-between items-center shrink-0 shadow-xs">
        <div className="flex items-center gap-3">
          <Store className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <span className="font-bold text-slate-900 dark:text-white text-sm tracking-tight">
            {localStorage.getItem('POS_LOCATION_NAME') || localStorage.getItem('COMPANY_NAME') || 'POS Terminal'}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {/* 1. Cashier (Clickable) */}
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent('open-pos-panel', { detail: 'profile' }));
            }}
            className="text-right hover:opacity-80 transition cursor-pointer select-none"
            title="Открыть профиль"
          >
            <p className="text-xs text-slate-500 dark:text-slate-400">Кассир</p>
            <p className="text-sm font-medium text-slate-900 dark:text-white">{user.name || 'Кассир'}</p>
          </button>

          {/* 2. Date and Time */}
          <div className="text-right select-none">
            <p className="text-xs text-slate-500 dark:text-slate-400">{dateStr}</p>
            <p className="text-sm font-mono font-bold text-emerald-600 dark:text-emerald-400">{timeStr}</p>
          </div>

          {/* 3. Bell Notifications */}
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent('open-pos-panel', { detail: 'notifications' }));
            }}
            className="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white rounded-sm transition border border-slate-200 dark:border-slate-700 relative"
            title="Уведомления"
          >
            <Bell className="w-4 h-4" />
            {unreadNotifs > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-[10px] font-black rounded-full flex items-center justify-center">
                {unreadNotifs}
              </span>
            )}
          </button>

          {/* 4. Lock Button */}
          <button
            onClick={() => {
              setIsLocked(true);
              toast('Касса заблокирована', { icon: '🔒' });
            }}
            className="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-sm transition border border-slate-200 dark:border-slate-600 group"
            title="Заблокировать кассу"
          >
            <Lock className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-red-600 dark:group-hover:text-red-400 transition" />
          </button>

          {/* Refresh App Button */}
          <button
            onClick={handleForceUpdate}
            className="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-sm transition border border-slate-200 dark:border-slate-600 group"
            title="Обновить приложение"
          >
            <RefreshCw className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition group-active:animate-spin" />
          </button>

          {/* 5. Network status (Non-clickable, no text) */}
          <div
            className={`flex items-center gap-1.5 px-2.5 py-2 rounded-sm border select-none transition ${
              isOnline
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
                : 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400'
            }`}
            title={isOnline ? 'Сеть подключена' : 'Офлайн режим'}
          >
            {isOnline ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4 animate-pulse" />}
            {offlinePending > 0 && (
              <span className="bg-amber-500 text-slate-950 font-black text-[10px] px-1 rounded-sm ml-1 animate-bounce">
                {offlinePending}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 flex overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
};

export default POSTerminalLayout;
